import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useParent } from '../../contexts/parentContext';
import ProfileImage from '../../components/profileImage';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const ChildControlEvents = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedChild } = useParent();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedChild) {
      fetchEvents();
    }
  }, [selectedChild]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching events for child:', selectedChild);
      
      const memberId = selectedChild?._id || selectedChild?.id || selectedChild?.userId;
      if (!memberId) {
        console.error('No member ID found for selected child');
        setError('No child selected or child ID not found');
        setEvents([]);
        return;
      }

      const response = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/event/get-all-events-by-team-member-id?memberId=${memberId}`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied: You may not have permission to view this child\'s events');
        }
        if (response.status === 404) {
          throw new Error('No events found for this child');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to fetch events: ${response.status} ${response.statusText}`);
      }

      const eventsData = await response.json();
      console.log('Events response:', eventsData);
      
      // The API returns events categorized by status: { live: [], upcoming: [], past: [] }
      // Combine all events into a single array for display
      const allEvents = [
        ...(eventsData.live || []).map(event => ({...event, eventStatus: 'In Progress'})),
        ...(eventsData.upcoming || []).map(event => ({...event, eventStatus: 'Upcoming'})),
        ...(eventsData.past || []).map(event => ({...event, eventStatus: 'Completed'}))
      ];
      
      setEvents(allEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for styling
  const getEventStatusInfo = (eventStatus) => {
    switch (eventStatus) {
      case 'In Progress':
        return {
          status: 'Live Now',
          color: 'text-yellow-800',
          bgColor: 'bg-yellow-100',
          dotColor: 'bg-yellow-500',
          icon: '🔴'
        };
      case 'Completed':
        return {
          status: 'Completed',
          color: 'text-green-800',
          bgColor: 'bg-green-100',
          dotColor: 'bg-green-500',
          icon: '✅'
        };
      case 'Upcoming':
      default:
        return {
          status: 'Upcoming',
          color: 'text-blue-800',
          bgColor: 'bg-blue-100',
          dotColor: 'bg-blue-500',
          icon: '📅'
        };
    }
  };

  const getEventTypeInfo = (eventType) => {
    switch (eventType) {
      case 'Match':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          ),
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      case 'Training':
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
            </svg>
          ),
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
      default:
        return {
          icon: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
            </svg>
          ),
          color: 'text-purple-600',
          bgColor: 'bg-purple-100'
        };
    }
  };

  const handleViewEvent = (eventId) => {
    navigate(`/parent/event/${eventId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <LoadingSpinner message={t("parent.loadingEvents")} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            {selectedChild?.username || selectedChild?.firstName}'s {t("parent.events")}
          </h1>
          <p className="text-lg text-gray-600">
            {t("parent.eventsDescription")}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <ErrorMessage message={error} onRetry={fetchEvents} />
          </div>
        )}

        {/* Events List */}
        <div className="space-y-8">
          {/* Events Grid */}
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-600">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">
                  {t("parent.yourChildEvents")}
                </h2>
                <div className="text-orange-100">
                  {events.length > 0 && (
                    <span className="text-sm">
                      {t("common.totalEvents", { count: events.length })}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Events List */}
            <div className="p-6">
              {!error && events.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zM4 7h12v1H4V7z"/>
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">{t("parent.noEvents")}</h4>
                  <p className="text-gray-500">{t("parent.noEventsDescription")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {events.map((event) => {
                    const statusInfo = getEventStatusInfo(event.eventStatus);
                    const typeInfo = getEventTypeInfo(event.eventType);

                    // Get participants count
                    const getParticipantsCount = () => {
                      return event.participantsCount || event.metaData?.totalMembers || event.members?.length || 0;
                    };

                    return (
                      <div key={event._id || event.id} className="bg-white border border-gray-200 rounded-xl hover:shadow-xl transition-all duration-300 overflow-hidden group hover:border-orange-200">
                        {/* Event Header */}
                        <div className="p-6 pb-4">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3 flex-1">
                              <div className={`${typeInfo.bgColor} ${typeInfo.color} p-3 rounded-xl group-hover:scale-110 transition-transform duration-200`}>
                                {typeInfo.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-xl font-bold text-gray-900 line-clamp-1 group-hover:text-orange-600 transition-colors duration-200">
                                  {event.name || event.title}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  {event.eventType} • {getParticipantsCount()} {t("common.participants")}
                                </p>
                              </div>
                            </div>
                            <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${statusInfo.bgColor} ${statusInfo.color} border border-current`}>
                              <div className={`w-2 h-2 rounded-full mr-2 ${statusInfo.dotColor} animate-pulse`}></div>
                              {statusInfo.status}
                            </div>
                          </div>

                          {/* Event Description */}
                          {event.description && (
                            <div className="bg-gray-50 rounded-lg p-4 mb-4">
                              <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
                                {event.description}
                              </p>
                            </div>
                          )}

                          {/* Event Details */}
                          <div className="space-y-4">
                            {/* Date and Time */}
                            <div className="flex items-center text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
                              <div className="bg-blue-100 p-2 rounded-lg mr-3">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">
                                  {new Date(event.startDate).toLocaleDateString('en-US', { 
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </div>
                                <div className="text-blue-700">
                                  {new Date(event.startDate).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                  {event.endDate && (
                                    <span> - {new Date(event.endDate).toLocaleTimeString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}</span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Location */}
                            {event.metaData?.location && (
                              <div className="flex items-center text-sm text-gray-600 bg-green-50 rounded-lg p-3">
                                <div className="bg-green-100 p-2 rounded-lg mr-3">
                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">Location</div>
                                  <div className="text-green-700">{event.metaData.location}</div>
                                </div>
                              </div>
                            )}

                            {/* Team Information */}
                            {event.team && (
                              <div className="flex items-center text-sm text-gray-600 bg-purple-50 rounded-lg p-3">
                                <div className="bg-purple-100 p-2 rounded-lg mr-3">
                                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 919.288 0M15 7a3 3 0 11-6 0 3 3 0 616 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                  </svg>
                                </div>
                                <div className="flex items-center">
                                  {event.team.logo && (
                                    <img 
                                      src={event.team.logo} 
                                      alt={event.team.name} 
                                      className="w-6 h-6 rounded-full mr-2" 
                                    />
                                  )}
                                  <div>
                                    <div className="font-medium text-gray-900">{event.team.name}</div>
                                    <div className="text-purple-700">{event.team.sport || 'Team'}</div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Action Button */}
                          <div className="mt-6 pt-4 border-t border-gray-100">
                            <button
                              onClick={() => handleViewEvent(event._id || event.id)}
                              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg flex items-center justify-center"
                            >
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {t("parent.viewDetails")}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildControlEvents; 